import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { deviceAPI, deviceTypeAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';

const Devices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [urlFilter, setUrlFilter] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [filters, setFilters] = useState({
    deviceTypeId: '',
    status: '',
    hasAssignment: '',
    location: '',
    department: '',
    sortBy: '',
    sortOrder: 'desc'
  });

  // URL parametrelerini oku ve filtreleri ayarla
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const department = params.get('department');
    const type = params.get('type');
    const unassigned = params.get('unassigned');

    if (department || type || unassigned) {
      setUrlFilter({ department, type, unassigned: unassigned === 'true' });
      setFilters(prev => ({
        ...prev,
        department: department || '',
        hasAssignment: unassigned === 'true' ? 'false' : prev.hasAssignment,
      }));
      if (type) {
        setSearch(type);
      }
    }
    setInitialLoadDone(true);
  }, [location.search]);

  useEffect(() => {
    fetchDeviceTypes();
    fetchLocations();
  }, []);

  // Sadece initialLoadDone true olduktan sonra fetch yap
  useEffect(() => {
    if (initialLoadDone) {
      fetchDevices();
    }
  }, [filters, initialLoadDone, search]);

  const fetchDeviceTypes = async () => {
    try {
      const response = await deviceTypeAPI.getAll({ isActive: true });
      setDeviceTypes(response.data);
    } catch (error) {
      console.error('Device types fetch error:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await deviceAPI.getLocations();
      setLocations(response.data);
    } catch (error) {
      console.error('Locations fetch error:', error);
    }
  };

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (search) params.search = search;
      Object.keys(params).forEach(key => !params[key] && delete params[key]);

      const response = await deviceAPI.getAll(params);
      setDevices(response.data);
    } catch (error) {
      toast.error('Cihazlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDevices();
  };

  const handleExportPDF = async () => {
    try {
      const response = await deviceAPI.getListPDF(filters);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cihaz_listesi.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF oluşturuldu');
    } catch (error) {
      toast.error('PDF oluşturulurken hata oluştu');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aktif' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pasif' },
      maintenance: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Bakımda' },
      retired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Emekli' },
      donated: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Bağış' },
      scrapped: { bg: 'bg-red-100', text: 'text-red-800', label: 'HEK' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getLifespanBadge = (remaining) => {
    if (remaining === null) return null;
    if (remaining === 0) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Ömür Doldu</span>;
    }
    if (remaining <= 6) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">{remaining} ay</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">{remaining} ay</span>;
  };

  const clearFilters = () => {
    setUrlFilter(null);
    setSearch('');
    setFilters({
      deviceTypeId: '',
      status: '',
      hasAssignment: '',
      location: '',
      department: '',
      sortBy: '',
      sortOrder: 'desc'
    });
    navigate('/devices');
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cihazlar</h1>
          {urlFilter && (urlFilter.department || urlFilter.type || urlFilter.unassigned) && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-600">
                Filtre: {urlFilter.department && <span className="font-medium text-blue-600">{urlFilter.department}</span>}
                {urlFilter.type && <span className="font-medium text-green-600">{urlFilter.type}</span>}
                {urlFilter.unassigned && <span className="font-medium text-orange-600">Atanmamış Cihazlar</span>}
              </span>
              <button
                onClick={clearFilters}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Filtreyi Temizle
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportPDF}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            PDF
          </button>
          <Link
            to="/devices/new"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Yeni Cihaz
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Ara (isim, marka, model, seri no)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </form>

          <div className="flex gap-2 flex-wrap">
            <select
              value={filters.deviceTypeId}
              onChange={(e) => setFilters({ ...filters, deviceTypeId: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tüm Türler</option>
              {deviceTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
              <option value="maintenance">Bakımda</option>
              <option value="retired">Emekli</option>
              <option value="donated">Bağış</option>
              <option value="scrapped">HEK</option>
            </select>

            <select
              value={filters.hasAssignment}
              onChange={(e) => setFilters({ ...filters, hasAssignment: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tüm Zimmet</option>
              <option value="true">Zimmetli</option>
              <option value="false">Zimmetsiz</option>
            </select>

            <select
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tüm Lokasyonlar</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>

            <select
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Sırala</option>
              <option value="age">Cihaz Yaşı</option>
              <option value="brand">Marka</option>
              <option value="purchaseDate">Alım Tarihi</option>
            </select>

            {filters.sortBy && (
              <button
                onClick={() => setFilters({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                title={filters.sortOrder === 'asc' ? 'Artan' : 'Azalan'}
              >
                <ArrowsUpDownIcon className="h-5 w-5" />
                {filters.sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Device List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Cihaz bulunamadı
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cihaz</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tür</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Seri No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kalan Ömür</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {devices.map((device) => (
                  <tr key={device.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">{device.name}</div>
                        <div className="text-sm text-gray-500">{device.brand} {device.model}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {device.deviceType?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {device.serialNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {device.assignedUser ? (
                        <div>
                          <div className="text-sm text-gray-900">{device.assignedUser.fullName}</div>
                          <div className="text-xs text-gray-500">{device.assignedUser.departmentInfo?.name || device.assignedUser.department}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Zimmetli değil</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getLifespanBadge(device.remainingLifespan)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(device.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/devices/${device.id}`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Detay
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Devices;
